import React, { memo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Typography,
  Box,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import type {
  CategoryCounts,
  ReservationSlotTemplate,
  TreatmentCategory,
} from '../../../../domain/reservationSlots';
import {
  TREATMENT_CATEGORIES,
  summarizeIntervalCategories,
} from '../../../../domain/reservationSlots';
import CategoryChip from './CategoryChip';

export interface TemplateListTableProps {
  templates: ReservationSlotTemplate[];
  onEdit: (template: ReservationSlotTemplate) => void;
  onDelete: (template: ReservationSlotTemplate) => void;
}

const PATTERN_LABELS: Record<string, string> = {
  single: '単一',
  alternate: '交互（A/B）',
};

/**
 * カテゴリ別枠数から、枠数が1以上のカテゴリを表示順で抽出する
 *
 * @param {CategoryCounts} counts - カテゴリ別の枠数
 * @returns {TreatmentCategory[]} 表示対象のカテゴリ配列
 */
function pickVisibleCategories(counts: CategoryCounts): TreatmentCategory[] {
  return TREATMENT_CATEGORIES.filter((category) => (counts[category] ?? 0) > 0);
}

/**
 * 1区間あたりのカテゴリ構成を表示するセル内容
 *
 * @description
 * 単一パターンは「ピル ×2」、交互パターンは「ピル ×2 スキン ×1 / ウェルクリ ×1」のように
 * A構成とB構成を「/」で区切って並べる。
 */
const CategoryCompositionCell: React.FC<{ template: ReservationSlotTemplate }> = ({ template }) => {
  const { a, b } = summarizeIntervalCategories(template);
  const categoriesA = pickVisibleCategories(a);
  const categoriesB = b === null ? [] : pickVisibleCategories(b);

  if (categoriesA.length === 0 && categoriesB.length === 0) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
      {categoriesA.map((category) => (
        <CategoryChip key={`a-${category}`} category={category} count={a[category]} />
      ))}
      {b !== null && (
        <>
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ mx: 0.25 }}
          >
            /
          </Typography>
          {categoriesB.map((category) => (
            <CategoryChip key={`b-${category}`} category={category} count={b[category]} />
          ))}
        </>
      )}
    </Box>
  );
};

/**
 * 解放枠テンプレート一覧テーブル
 *
 * @description
 * テンプレートの一覧を表示し、各行に編集・削除ボタンを備える。
 * 1区間あたりの枠構成は「カテゴリ構成」列にカテゴリチップで集約表示する。
 */
const TemplateListTable: React.FC<TemplateListTableProps> = memo(
  ({ templates, onEdit, onDelete }) => {
    if (templates.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          テンプレートが登録されていません。「新規作成」から追加してください。
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>テンプレート名</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 80 }}>枠間隔</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 100 }}>パターン</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 240 }}>カテゴリ構成</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 100 }}>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id} hover>
                <TableCell sx={{ fontWeight: 500 }}>{template.name}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{template.intervalMinutes}分</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {PATTERN_LABELS[template.patternType] ?? template.patternType}
                </TableCell>
                <TableCell>
                  <CategoryCompositionCell template={template} />
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Tooltip title="編集">
                    <IconButton size="small" onClick={() => onEdit(template)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="削除">
                    <IconButton size="small" color="error" onClick={() => onDelete(template)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
);

TemplateListTable.displayName = 'TemplateListTable';

export default TemplateListTable;
